/**
 * `<SectionBadge>` — chip visual estandarizado para una sección de club
 * (Aventureros / Conquistadores / Guías Mayores). Usa la paleta oficial
 * definida en `constants/sections.ts` y respeta la accesibilidad.
 */

import { cn } from "@/lib/utils";
import {
  SECTION_CHIP_CLASSES,
  SECTION_CHIP_SOFT_CLASSES,
} from "@/lib/constants/sections";
import { CLUB_TYPE_LABELS, type ClubType } from "@/types";

export interface SectionBadgeProps {
  section: ClubType;
  variant?: "solid" | "soft";
  size?: "xs" | "sm" | "md";
  className?: string;
  /** Etiqueta corta (Av / Con / GM) útil en tablas densas o móvil. */
  short?: boolean;
}

const SHORT_LABELS: Record<ClubType, string> = {
  aventureros: "Av",
  conquistadores: "Con",
  guias_mayores: "GM",
};

const SIZES: Record<NonNullable<SectionBadgeProps["size"]>, string> = {
  xs: "px-1.5 py-0.5 text-[10px]",
  sm: "px-2 py-0.5 text-xs",
  md: "px-2.5 py-1 text-xs",
};

export function SectionBadge({
  section,
  variant = "soft",
  size = "sm",
  className,
  short = false,
}: SectionBadgeProps) {
  const palette =
    variant === "soft" ? SECTION_CHIP_SOFT_CLASSES : SECTION_CHIP_CLASSES;
  const label = short ? SHORT_LABELS[section] : CLUB_TYPE_LABELS[section];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 font-semibold rounded-full whitespace-nowrap",
        palette[section],
        SIZES[size],
        className,
      )}
      aria-label={CLUB_TYPE_LABELS[section]}
    >
      {label}
    </span>
  );
}

export default SectionBadge;
